import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { IdTokenClaims, UserContextType } from "../types";
import { InternalUserRoles, Roles } from "../constants/roles";

const defaultUserContext: UserContextType = {
  name: "",
  username: "",
  idTokenClaims: {},
  isInternalUser: false,
  isGuestUser: false,
};

export const UserContext = createContext<UserContextType>(defaultUserContext);

interface UserContextProviderProps {
  children: React.ReactNode;
}

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const { identity, hasAnyRole } = useAuth();
  const [name, setName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [idTokenClaims, setIdTokenClaims] = useState<IdTokenClaims>({});
  const [isInternalUser, setIsInternalUser] = useState<boolean>(false);
  const [isGuestUser, setIsGuestUser] = useState<boolean>(false);

  useEffect(() => {
    setName(identity?.name ?? "");
    setUsername(identity?.username ?? "");
    setIdTokenClaims(identity?.idTokenClaims ?? {});
    const isInternalUser = hasAnyRole(InternalUserRoles);
    setIsInternalUser(isInternalUser);
    const isGuestUser = hasAnyRole([Roles.MidasGuest]);
    setIsGuestUser(!isInternalUser && isGuestUser);
  }, [hasAnyRole, identity]);

  return (
    <UserContext.Provider value={{ name, username, idTokenClaims, isInternalUser, isGuestUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserContextProvider");
  }
  return context;
};
